/**
 * Helper functions for working with Superset data and charts
 */
import axios from 'axios';

interface TimeRange {
  currentMonthStartDate?: number;
  currentMonthEndDate?: number;
}

interface ChartParams {
  timeRange?: TimeRange;
  selectedInterval?: string;
  filters?: Array<{
    col: string;
    op: string;
    val: any;
  }>;
  timeGrain?: string;
  columns?: Array<any>;
  timeOffsets?: Array<string>;
}

interface DashboardChart {
  chart_id: string;
  chart_name: string;
}

interface Dashboard {
  dashboard_id: string;
  dashboard_name: string;
  charts: DashboardChart[];
}

interface DashboardConfig {
  setting_type: string;
  datasource: {
    id: number;
    type: string;
  };
  dashboards: Dashboard[];
}

interface DashboardResponse {
  timestamp: string;
  status: number;
  message: string;
  data: DashboardConfig;
}

/**
 * Get a specific chart ID from dashboard data
 * @param chartName The name of the chart to find
 * @param dashboardName The name of the dashboard containing the chart
 * @param allChartId The dashboard data containing all charts
 * @returns The chart ID if found, null otherwise
 */
export const getChartId = (chartName: string, dashboardName: string, allChartId?: any) => {
  if (!allChartId) return null;

  const dashboard = allChartId.find(
    (d: any) => d.dashboard_name === dashboardName
  );
  if (!dashboard) return null;

  const chart = dashboard.charts.find(
    (c: any) => c.chart_name === chartName
  );
  return chart?.chart_id;
};

/**
 * Fetch chart configuration from Superset
 * @param chartId The ID of the chart
 * @param headers Authentication headers
 * @returns Chart configuration data
 */
export const fetchChartConfig = async (chartId: number, headers: any) => {
  const api = axios.create({
    baseURL: process.env.REACT_APP_SUPERSET_URL,
    withCredentials: true,
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
      'X-Requested-With': 'XMLHttpRequest',
      'wizr-access-key': process.env.REACT_APP_API_AUTHORIZATION_HEADER_APIKEY_VALUE || '',
      ...headers
    }
  });

  const chartUrl = process.env.REACT_APP_SUPERSET_CHART_API?.replace('{chart_id}', String(chartId)) || '';
  try {
    const response = await api.get(chartUrl);
    return response.data;
  } catch (error) {
    console.error('Error fetching chart configuration:', error);
    throw error;
  }
};

/**
 * Fetch chart data from Superset
 * @param chartId The ID of the chart
 * @param queryContext The query context from chart configuration
 * @param params Additional parameters for the query
 * @param headers Authentication headers
 * @returns Chart data
 */
export const fetchChartData = async (
  chartId: number,
  queryContext: any,
  params: ChartParams,
  headers: any
) => {
  const api = axios.create({
    baseURL: process.env.REACT_APP_SUPERSET_URL,
    withCredentials: true,
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
      'X-Requested-With': 'XMLHttpRequest',
      ...headers
    }
  });

  const chartDataUrl = process.env.REACT_APP_SUPERSET_CHART_DATA_API || '';
  try {
    const dataResponse = await api.post(chartDataUrl, {
      datasource: queryContext.datasource,
      queries: [{
        ...queryContext.queries[0],
        filters: params.filters || [],
        extras: {
          time_grain_sqla: params.timeGrain || 'day',
          having: "",
          where: ""
        },
        columns: params.columns || [],
        time_offsets: params.timeOffsets || []
      }],
      form_data: {
        ...queryContext.form_data,
        slice_id: chartId,
        force: false,
        time_grain_sqla: params.timeGrain || 'day',
        result_format: 'json',
        result_type: 'full'
      }
    });

    return dataResponse.data?.result?.[0]?.data || [];
  } catch (error) {
    console.error('Error fetching chart data:', error);
    throw error;
  }
};

/**
 * Dashboard names used in the application
 */
export const DASHBOARD_NAMES = {
  ORGANIZATION_SUMMARY: "Organization Summary",
  CUSTOMER_HEALTH: "Customer Health",
  AGENT_INSIGHTS: "Agent Insights",
  DISCOVER_PAGE: "Discover",
} as const;

/**
 * Chart names used in the application
 */
export const CHART_NAMES = {
  CUSTOMER_HEALTH_LIST: "Customer Health",
 
  CUSTOMER_HEALTH_TREND: "Customer Health Trend",
  CUSTOMER_HEALTH_TOP_INTENTS: "Top Intents",
  CUSTOMER_HEALTH_SENTIMENT_AVG: "Org Avg Sentiment",
  CUSTOMER_HEALTH_RESOLUTION_TIME_AVG: "Org Avg Time to Resolve",
  INTENTS: "Intents",
  AGENT_INSIGHTS_AVG_RESPONSE_TIME_COUNT: "Avg Response Time Count",
  AGENT_INSIGHTS_AVG_RESOLUTION_RESPONSE_TIME: "Resolution Time Count",
   AGENT_INSIGHTS_RESOLUTION_RATE: "Resolution Rate Count",
  
  AGENT_INSIGHTS_SENTIMENT_SCORE: "Sentiment Score Count",
  AGENT_INSIGHTS_AGENT_PERFORMANCE: "Agent Performance",
  AGENT_INSIGHTS_CUSTOMER_SENTIMENT_HEATMAP: "Performance by Customer Sentiment",
  AGENT_INSIGHTS_PERFORMANCE_BREAKDOWN_HEATMAP: "Agent Performance Breakdown",

  DISCOVER_SUPERSET: "Discover",
} as const;

/**
 * Get datasource ID from dashboard configuration
 * @param dashboardConfig The dashboard configuration response
 * @returns The datasource ID if found, null otherwise
 */
export const getDatasourceId = (dashboardConfig: DashboardResponse): number | null => {
  return dashboardConfig?.data?.datasource?.id || null;
};

/**
 * Update query context with new datasource ID
 * @param queryContext The original query context
 * @param datasourceId The new datasource ID to use
 * @returns Updated query context with new datasource ID
 */
export const updateQueryContextDatasource = (queryContext: any, datasourceId: number) => {
  if (!queryContext) return queryContext;
  
  return {
    ...queryContext,
    datasource: {
      ...queryContext.datasource,
      id: datasourceId
    }
  };
}; 